import React, { useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";

import dashboardStyle from "assets/jss/material-dashboard-react/views/dashboardStyle.js";
import useEstadis from "../../hooks/useEstadis";
import { formatCurrency } from "../../utils/formatCurrency";

import SnackBarWiiba from "components/Snackbar/SnackBarWiiba";
import useMessage from "../../hooks/useMessage";

// core components
import Icon from "@material-ui/core/Icon";
import DateRange from "@material-ui/icons/DateRange";
import LocalOffer from "@material-ui/icons/LocalOffer";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import CardIcon from "components/Card/CardIcon.js";
import CardFooter from "components/Card/CardFooter.js";

import EstadisMoratorio from "./EstadisMoratorio";
import EstadisProyeccionEfe from "./EstadisProyeccionEfe";

// New chart components
import DonutChart from "./charts/DonutChart";
import GroupedBarChart from "./charts/GroupedBarChart";
import AreaLineChart from "./charts/AreaLineChart";
import HorizontalBarChart from "./charts/HorizontalBarChart";

const useStyles = makeStyles(() => ({
  ...dashboardStyle,
  root: {
    width: "100%"
  },
  cardIngresos: {
    fontWeight: "bold"
  }
}));

export default function Estadisticas() {
  const classes = useStyles();
  const [proyectaEfectivoDia, setProyectaEfectivoDia] = useState([]);
  const [proyectaEfectivoMes, setProyectaEfectivoMes] = useState([]);

  // Fechas dinámicas: primer día del mes actual → hoy
  const hoy = new Date();
  const primerDiaMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const formatDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const formatDateDMY = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${day}-${m}-${y}`;
  };

  const [fechaIni, setFechaIni] = useState(formatDate(primerDiaMes));
  const [fechaFin, setFechaFin] = useState(formatDate(hoy));

  const {
    opensnack,
    typesnackbar,
    message,
    handleMuestraSnack,
    handleClosesSnack
  } = useMessage();

  const {
    estadisticas,
    getEstadisticas,
    fechasM,
    getEstadisticasMontoRango,
    estadisLotes,
    getEstaisticasLotes,
    estadisClientes,
    getEstadisticasClientes,
    totalAmortizaciones,
    getAmortizacionesTotales,
    getProyeccionEfectivo
  } = useEstadis({ handleMuestraSnack });

  const MIN_DATE = "2020-01-01";
  const MAX_DATE = formatDate(hoy);

  const cargarDatos = () => {
    // BUG-D6: Validar que fecha fin >= fecha inicio
    if (fechaFin < fechaIni) {
      handleMuestraSnack("La fecha fin no puede ser menor que la fecha inicio", "warning");
      return;
    }
    // BUG-D7/D8: Validar rango de fechas
    if (fechaIni > MAX_DATE || fechaFin > MAX_DATE) {
      handleMuestraSnack("No se permiten fechas futuras", "warning");
      return;
    }
    if (fechaIni < MIN_DATE || fechaFin < MIN_DATE) {
      handleMuestraSnack("La fecha mínima permitida es 01/01/2020", "warning");
      return;
    }
    const ini = `${fechaIni} 00:00:00`;
    const fin = `${fechaFin} 23:59:59`;
    getEstadisticas(ini, fin);
    getEstadisticasMontoRango(ini, fin);
    getEstaisticasLotes();
    getEstadisticasClientes();
    getAmortizacionesTotales();
    getProyeccionEfectivo(formatDateDMY(hoy), formatDateDMY(hoy), setProyectaEfectivoDia);
    getProyeccionEfectivo(formatDateDMY(primerDiaMes), formatDateDMY(hoy), setProyectaEfectivoMes);
  };

  useEffect(() => {
    cargarDatos();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatEstatus = (desc) => {
    if (!desc) return desc;
    const map = {
      "CREDITOACTIVO": "Crédito Activo",
      "DISPONIBLE": "Disponible",
      "PENDIENTE": "Pendiente",
      "LIQUIDADO": "Liquidado",
      "APARTADO": "Apartado"
    };
    return map[desc.toUpperCase()] || desc;
  };

  function renderListItems() {
    return estadisLotes.map(item => {
      let color = "black";
      const desc = (item.DESCRIPCION || "").toUpperCase();
      if (desc === "APARTADO") color = "orange";
      else if (desc === "CREDITOACTIVO") color = "green";
      else if (desc === "DISPONIBLE") color = "blue";
      else if (desc === "LIQUIDADO") color = "gray";
      else if (desc === "PENDIENTE") color = "red";
      return (
        <li key={item.DESCRIPCION} style={{ color, fontWeight: "bold" }}>
          {formatEstatus(item.DESCRIPCION)}: {item.TOTAL != null ? item.TOTAL : 0}
        </li>
      );
    });
  }

  return (
    <div className={classes.root}>
      <SnackBarWiiba
        opensnack={opensnack}
        tipoSnackBar={typesnackbar}
        mensaje={message}
        handleClosesSnack={handleClosesSnack}
      />
      <div>
        {/* Filtros de fecha */}
        <GridContainer>
          <GridItem xs={12}>
            <Card>
              <CardBody>
                <GridContainer alignItems="center">
                  <GridItem xs={12} sm={4} md={3}>
                    <label style={{ fontSize: 12, color: "#999" }}>Fecha inicio</label>
                    <input
                      type="date"
                      value={fechaIni}
                      onChange={e => setFechaIni(e.target.value)}
                      min={MIN_DATE}
                      max={MAX_DATE}
                      style={{ width: "100%", padding: 8, fontSize: 14, border: "1px solid #ccc", borderRadius: 4 }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={4} md={3}>
                    <label style={{ fontSize: 12, color: "#999" }}>Fecha fin</label>
                    <input
                      type="date"
                      value={fechaFin}
                      onChange={e => setFechaFin(e.target.value)}
                      min={MIN_DATE}
                      max={MAX_DATE}
                      style={{ width: "100%", padding: 8, fontSize: 14, border: "1px solid #ccc", borderRadius: 4 }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={4} md={3} style={{ paddingTop: 16 }}>
                    <button
                      onClick={cargarDatos}
                      style={{
                        padding: "8px 24px", fontSize: 14, backgroundColor: "#00acc1",
                        color: "#fff", border: "none", borderRadius: 4, cursor: "pointer"
                      }}
                    >
                      Actualizar
                    </button>
                  </GridItem>
                </GridContainer>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>

        {/* Row 1: KPI Cards + Projection */}
        <GridContainer>
          <GridItem xs={12} sm={6} md={4}>
            <Card>
              <CardHeader color="primary" stats icon>
                <CardIcon color="primary">
                  <Icon>
                    <a href="#" className={classes.cardLink}>
                      attach_money
                    </a>
                  </Icon>
                </CardIcon>
                <br />
                <h3 className={classes.cardTitle}>
                  Totales
                </h3>
              </CardHeader>
              <CardBody>
                <br />
                <h4>Mes corriente</h4>
                <h5 className={classes.cardIngresos}>
                  Efectivo: {formatCurrency(estadisticas ? estadisticas.EFECTIVO : 0)}
                </h5>
                <h5 className={classes.cardIngresos}>
                  Tarjeta: {formatCurrency(estadisticas ? estadisticas.TARJETA : 0)}
                </h5>
                <h5 className={classes.cardIngresos}>
                  Transferencia: {formatCurrency(estadisticas ? estadisticas.TRANSFERENCIA : 0)}
                </h5>
              </CardBody>
              <CardFooter stats>
                <div className={classes.stats}>
                  <DateRange />
                  Entrada del dia
                </div>
              </CardFooter>
            </Card>
          </GridItem>
          <GridItem xs={12} sm={6} md={4}>
            <Card>
              <CardHeader color="primary" stats icon>
                <CardIcon color="primary">
                  <Icon>
                    <a href="#" className={classes.cardLink}>
                      access_time
                    </a>
                  </Icon>
                </CardIcon>
                <br />
                <h3 className={classes.cardTitle}>Resumen lotes</h3>
              </CardHeader>
              <CardBody>
                <br />
                <ul>
                  <h5 className={classes.cardTitle}> {renderListItems()} </h5>
                </ul>
              </CardBody>
              <CardFooter stats>
                <div className={classes.stats}>
                  <DateRange />
                  últimas 24 Horas
                </div>
              </CardFooter>
            </Card>
          </GridItem>
          <GridItem xs={12} sm={6} md={4}>
            <Card>
              <CardHeader color="primary" stats icon>
                <CardIcon color="primary">
                  <Icon>
                    <a href="#" className={classes.cardLink}>
                      money_off
                    </a>
                  </Icon>
                </CardIcon>
                <br />
                <h3 className={classes.cardTitle}>Resumen clientes</h3>
              </CardHeader>
              <CardBody>
                <br />
                <h5
                  style={{ color: "green", fontWeight: "bold" }}
                  className={classes.cardTitle}
                >
                  Créditos activo: {estadisClientes.DISPONIBLE === undefined
                    ? 0
                    : estadisClientes.DISPONIBLE}
                </h5>
                <h5
                  style={{ color: "red", fontWeight: "bold" }}
                  className={classes.cardTitle}
                >
                  Moratorios: {estadisClientes.MORATORIO === undefined
                    ? 0
                    : estadisClientes.MORATORIO}
                </h5>
                <h5
                  style={{ color: "purple", fontWeight: "bold" }}
                  className={classes.cardTitle}
                >
                  Crédito activo: {estadisClientes.CREDITOACTIVO === undefined
                    ? 0
                    : estadisClientes.CREDITOACTIVO}
                </h5>
              </CardBody>
              <CardFooter stats>
                <div className={classes.stats}>
                  <LocalOffer />
                </div>
              </CardFooter>
            </Card>
          </GridItem>

          <EstadisProyeccionEfe
            proyectaEfectivo={proyectaEfectivoDia}
            proyectaEfectivoMes={proyectaEfectivoMes}
          />
        </GridContainer>

        {/* Row 2: Charts */}
        <GridContainer>
          <GridItem xs={12} md={4}>
            <Card>
              <CardHeader color="primary">
                <h4 className={classes.cardTitle}>Distribución de Lotes</h4>
              </CardHeader>
              <CardBody>
                <DonutChart data={estadisLotes} />
              </CardBody>
            </Card>
          </GridItem>
          <GridItem xs={12} md={4}>
            <Card>
              <CardHeader color="primary">
                <h4 className={classes.cardTitle}>Ingresos por Método de Pago</h4>
              </CardHeader>
              <CardBody>
                <GroupedBarChart data={estadisticas} />
              </CardBody>
            </Card>
          </GridItem>
          <GridItem xs={12} md={4}>
            <Card>
              <CardHeader color="primary">
                <h4 className={classes.cardTitle}>Tendencia de Ingresos</h4>
              </CardHeader>
              <CardBody>
                <AreaLineChart data={fechasM} />
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>

        {/* Row 3: Moratory section */}
        <GridContainer>
          <GridItem xs={12} md={6}>
            <Card>
              <CardHeader color="primary">
                <h4 className={classes.cardTitle}>Top Clientes Moratorios</h4>
              </CardHeader>
              <CardBody>
                <HorizontalBarChart data={totalAmortizaciones} />
              </CardBody>
            </Card>
          </GridItem>
          <GridItem xs={12} md={6}>
            <EstadisMoratorio totalAmortizaciones={totalAmortizaciones} />
          </GridItem>
        </GridContainer>
      </div>
    </div>
  );
}
